import React from 'react';
import {
  Box, Typography, IconButton, Checkbox, alpha, useTheme, TextField,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';

function Row({
  item, selected, dropTarget, inlineRenameOf,
  onClick, onDoubleClick, onContextMenu, onMoreClick, onToggleSelect,
  onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
  onInlineRenameCommit, onInlineRenameCancel,
  darkMode,
}) {
  const theme = useTheme();
  const isFolder = item.type === 'folder';
  const editing = inlineRenameOf === item.logicalPath;
  const [draftName, setDraftName] = React.useState(item.name);
  React.useEffect(() => { if (editing) setDraftName(item.name); }, [editing, item.name]);

  return (
    <Box
      draggable={!editing}
      onDragStart={(e) => onDragStart(e, item)}
      onDragEnd={onDragEnd}
      onDragOver={isFolder ? (e) => { e.preventDefault(); onDragOver(item); } : undefined}
      onDragLeave={isFolder ? () => onDragLeave(item) : undefined}
      onDrop={isFolder ? (e) => { e.preventDefault(); onDrop(item, e); } : undefined}
      onClick={(e) => onClick(item, e)}
      onDoubleClick={() => onDoubleClick(item)}
      onContextMenu={(e) => onContextMenu(e, item)}
      sx={{
        display: 'grid',
        gridTemplateColumns: '36px 24px 1fr 110px 180px 40px',
        alignItems: 'center', gap: 1, px: 1, py: 0.75, borderRadius: 1,
        cursor: 'pointer',
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.12)
          : dropTarget
            ? alpha(theme.palette.primary.main, 0.16)
            : 'transparent',
        border: '1px solid',
        borderColor: dropTarget ? theme.palette.primary.main : 'transparent',
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <Checkbox
        size="small" checked={selected}
        onClick={(e) => { e.stopPropagation(); onToggleSelect(item); }}
        onChange={() => {}}
      />
      {isFolder
        ? <FolderIcon color="primary" fontSize="small" />
        : <InsertDriveFileIcon fontSize="small" />}
      {editing ? (
        <TextField
          size="small" autoFocus value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Enter') onInlineRenameCommit(item, draftName);
            if (e.key === 'Escape') onInlineRenameCancel();
          }}
          onBlur={() => onInlineRenameCommit(item, draftName)}
        />
      ) : (
        <Typography variant="body2" noWrap title={item.name}>{item.name}</Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        {isFolder ? '—' : item.sizeLabel}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {item.lastModified ? new Date(item.lastModified).toLocaleString() : '—'}
      </Typography>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMoreClick(e, item); }}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

export default function ListView({
  folders, files,
  selectedPaths, dropTargetPath, inlineRenameOf,
  onItemClick, onItemDoubleClick, onItemContextMenu, onItemMore,
  onToggleSelect,
  onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
  onInlineRenameCommit, onInlineRenameCancel,
  darkMode,
  emptyHint,
}) {
  const theme = useTheme();
  const isEmpty = folders.length === 0 && files.length === 0;
  return (
    <Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '36px 24px 1fr 110px 180px 40px',
        alignItems: 'center', gap: 1, px: 1, py: 0.5,
        borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
        color: 'text.secondary', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        <Box />
        <Box />
        <Box>Name</Box>
        <Box>Size</Box>
        <Box>Modified</Box>
        <Box />
      </Box>
      {[...folders, ...files].map((item) => (
        <Row
          key={item.logicalPath}
          item={item}
          selected={selectedPaths.has(item.logicalPath)}
          dropTarget={dropTargetPath === item.logicalPath}
          inlineRenameOf={inlineRenameOf}
          onClick={onItemClick} onDoubleClick={onItemDoubleClick} onContextMenu={onItemContextMenu}
          onMoreClick={onItemMore} onToggleSelect={onToggleSelect}
          onDragStart={onDragStart} onDragEnd={onDragEnd}
          onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
          onInlineRenameCommit={onInlineRenameCommit} onInlineRenameCancel={onInlineRenameCancel}
          darkMode={darkMode}
        />
      ))}
      {isEmpty && (
        <Box sx={{
          textAlign: 'center', py: 8, px: 3,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>This folder is empty</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
            {emptyHint || 'Drop files here, paste an image with ⌘V, or use the Upload button.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
